/**
 * The Ask eve panel's rules, without React: which chat it opens on a
 * workflow, what it remembers per person, how wide it may be, and what its
 * empty chat offers for the chart on screen.
 *
 * @module components/workflows/AskEvePanel.logic
 */
import { scopeThreadRef } from "@amoreg/client-runtime/environment";
import { type EnvironmentId, type ScopedThreadRef, ThreadId, type WorkflowChat, type WorkflowGraph } from "@amoreg/contracts";
import * as Schema from "effect/Schema";

import type { DraftId } from "../../composerDraftStore";

/**
 * The chat on screen. A new chat is a draft until its first message starts
 * the thread; both carry the thread's ref, because the workflow is bound to
 * the thread id before that first message is sent.
 */
export type PanelChat =
  | { readonly kind: "thread"; readonly threadRef: ScopedThreadRef }
  | { readonly kind: "draft"; readonly draftId: DraftId; readonly threadRef: ScopedThreadRef };

/** What the chat host shows for the panel's chat. */
export const panelChatTarget = (chat: PanelChat | null) =>
  chat === null
    ? null
    : chat.kind === "thread"
      ? ({ kind: "server", threadRef: chat.threadRef } as const)
      : ({ kind: "draft", draftId: chat.draftId } as const);

/**
 * The newest chat the person opened from this workflow that can still be
 * shown: a started thread, or a new chat whose unsent draft (found by its
 * thread) is still in this browser. Null when there is none, and the panel
 * starts a new chat.
 */
export function pickPanelChat(
  chats: ReadonlyArray<WorkflowChat>,
  environmentId: EnvironmentId,
  draftFor: (threadRef: ScopedThreadRef) => DraftId | null,
): PanelChat | null {
  for (const chat of chats) {
    const threadRef = scopeThreadRef(environmentId, ThreadId.make(chat.threadId));
    if (chat.started) return { kind: "thread", threadRef };
    const draftId = draftFor(threadRef);
    if (draftId) return { kind: "draft", draftId, threadRef };
  }
  return null;
}

/** The person's started chats on this workflow, newest first, for "Recent chats". */
export const recentPanelThreads = (chats: ReadonlyArray<WorkflowChat>, environmentId: EnvironmentId) =>
  chats.flatMap((chat) => (chat.started ? [scopeThreadRef(environmentId, ThreadId.make(chat.threadId))] : []));

/**
 * The composer draft slot of a workflow's new chat: its own, so the panel's
 * unsent message never shows up in Home's composer, and Home's never here.
 * No colon: rehydrating, the draft store reads a slot with one as an
 * `environment:project` key and moves the draft to that project.
 */
export const workflowChatSlot = (organizationId: string, workflowId: string) =>
  `workflow-chat/${organizationId}/${workflowId}`;

export const PANEL_MIN_WIDTH = 420;
export const PANEL_DEFAULT_WIDTH = 440;
export const PANEL_MAX_WIDTH = 720;
/** The chart keeps at least this much of the page beside the panel: room for the 388 px step panel and a step beside it. */
export const CANVAS_MIN_WIDTH = 720;

/**
 * Whether a page this wide holds the chart's minimum and the panel's, so the
 * panel sits beside the chart; narrower, it opens over the chart, full width
 * with a Chart control, as on a phone.
 */
export const pageHoldsPanelBeside = (pageWidth: number): boolean => pageWidth >= CANVAS_MIN_WIDTH + PANEL_MIN_WIDTH;

/** A width the page can hold with the chart still usable beside it; with no page width, only the panel's own limits. */
export function clampPanelWidth(width: number, pageWidth = Number.POSITIVE_INFINITY): number {
  const widest = Math.max(PANEL_MIN_WIDTH, Math.min(PANEL_MAX_WIDTH, pageWidth - CANVAS_MIN_WIDTH));
  return Math.round(Math.min(widest, Math.max(PANEL_MIN_WIDTH, width)));
}

export const PanelPrefs = Schema.Struct({
  open: Schema.Boolean,
  width: Schema.Number,
});
export type PanelPrefs = typeof PanelPrefs.Type;

/** Each person's panel, keyed by who is signed in, so one browser can serve two people. */
export const PanelPrefsByPerson = Schema.Record(Schema.String, PanelPrefs);
export type PanelPrefsByPerson = typeof PanelPrefsByPerson.Type;

export const PANEL_PREFS_STORAGE_KEY = "amoreg:ask-eve-panel:v1";

export const CLOSED_PANEL: PanelPrefs = { open: false, width: PANEL_DEFAULT_WIDTH };

/** Who the preferences belong to: the directory's user, else the email, else this browser. */
export const panelPerson = (viewer: { readonly userId: string | null; readonly email: string | null }) =>
  viewer.userId ?? viewer.email ?? "";

export const panelPrefsFor = (stored: PanelPrefsByPerson, person: string): PanelPrefs => stored[person] ?? CLOSED_PANEL;

export const withPanelPrefs = (
  stored: PanelPrefsByPerson,
  person: string,
  change: Partial<PanelPrefs>,
): PanelPrefsByPerson => ({ ...stored, [person]: { ...panelPrefsFor(stored, person), ...change } });

/**
 * Where the panel opens: closed, no step's panel open, and room for the
 * panel beside the chart. A locked chart and a run show it too.
 */
export const askEveTabShown = (input: { readonly panelOpen: boolean; readonly stepOpen: boolean; readonly roomBeside: boolean }) =>
  !input.panelOpen && !input.stepOpen && input.roomBeside;

/**
 * Nothing built yet: no steps but Start and Output, or the starter chart a
 * new workflow is made with (Start and one Prompt with no instructions).
 */
export function isBlankChart(graph: Pick<WorkflowGraph, "nodes">): boolean {
  if (graph.nodes.every((node) => node.type === "start" || node.type === "output")) return true;
  if (graph.nodes.length !== 2) return false;
  const prompt = graph.nodes.find((node) => node.type === "prompt");
  return prompt?.type === "prompt" && prompt.config.instructions.trim() === "" && graph.nodes.some((node) => node.type === "start");
}

export type PanelChartState = "blank" | "steps" | "locked" | "run";

/** What the chart on screen is to the panel: a run shown, a chart this person cannot change now, or one they can, built or not. */
export function panelChartState(input: { readonly viewingRun: boolean; readonly editable: boolean; readonly graph: Pick<WorkflowGraph, "nodes"> }): PanelChartState {
  if (input.viewingRun) return "run";
  if (!input.editable) return "locked";
  return isBlankChart(input.graph) ? "blank" : "steps";
}

export interface PanelChartView {
  /** Sent as typed when picked from the empty chat. */
  readonly examples: ReadonlyArray<string>;
  readonly placeholder: string;
  /** One word above the composer; the composer stays enabled under it. */
  readonly note: string | null;
}

export const PANEL_CHART_VIEWS: Readonly<Record<PanelChartState, PanelChartView>> = {
  blank: {
    examples: [
      "Read the trade file each morning, send me the exceptions",
      "Draft a client letter, Carol approves, save as Word",
      "Add a manual import step after the draft",
    ],
    placeholder: "Describe the workflow",
    note: null,
  },
  steps: {
    examples: ["Explain this workflow", "Add an approval before the email", "What's left to fill in?"],
    placeholder: "Describe a change",
    note: null,
  },
  locked: {
    examples: ["Explain this workflow", "What's left to fill in?"],
    placeholder: "Describe a change",
    note: "Locked",
  },
  run: {
    examples: ["Why did this step fail?", "Summarise this run"],
    placeholder: "Describe a change",
    note: "Run",
  },
};

export const FIRST_VISITS_STORAGE_KEY = "amoreg:ask-eve-first-visits:v1";
/** The most workflows this browser remembers a first visit to; the oldest go first. */
export const FIRST_VISITS_KEPT = 500;
export const FirstVisits = Schema.Array(Schema.String);

const visitKey = (person: string, workflowId: string) => `${person}\n${workflowId}`;

/**
 * A person's first visit to a workflow in this browser, remembered: whether
 * it opens the panel (a blank chart they can build), and the visits to keep.
 * Null when this is not their first visit.
 */
export function firstVisit(
  visits: ReadonlyArray<string>,
  input: { readonly person: string; readonly workflowId: string; readonly state: PanelChartState },
): { readonly opens: boolean; readonly visits: ReadonlyArray<string> } | null {
  const key = visitKey(input.person, input.workflowId);
  if (visits.includes(key)) return null;
  return { opens: input.state === "blank", visits: [...visits, key].slice(-FIRST_VISITS_KEPT) };
}
